/**
 * InterruptBudget — v8.5.27 · F4 · "presupuesto de interrupción: 1 capa por sesión".
 *
 * Antes NPSModal, MoodPrompt y OnboardingTour decidían cada uno por su
 * cuenta cuándo mostrarse (timers y checks de API independientes) — nada
 * impedía que dos se solaparan en la misma carga (ej. un cliente nuevo con
 * NPS pendiente podía ver el onboarding Y la encuesta compitiendo). Ahora
 * cada uno pide turno acá y solo el de mayor prioridad se muestra; el resto
 * espera a que se libere (se cierre/complete) antes de mostrarse en la
 * misma sesión.
 *
 * ForcePasswordChange queda fuera a propósito: no es una interrupción
 * evitable, es un gate de seguridad bloqueante que ya no se puede cerrar.
 *
 * v8.5.64 · piel · "nunca sobre una pantalla de decisión" — el Provider
 * recibe el pathname actual (Layout.jsx ya lo tiene vía useLocation) y
 * calcula `blocked`. `granted` ahora exige !blocked además de tener el
 * turno: si el usuario está en /tareas (la lista), en la ficha de una
 * tarea o en el carrusel de revisión, ninguna interrupción se pinta —
 * sigue "activa" en la cola, así que aparece apenas el usuario navega a
 * otra pantalla, sin tener que rehacer el ciclo de request().
 */
import { createContext, useContext, useState, useRef, useCallback } from "react";

const Ctx = createContext(null);

const SUBRUTAS_TRABAJO_NO_DECISION = new Set(["pedidos", "campanas", "favoritos", "aprobar"]);

function esPantallaDeDecision(pathname) {
  if (pathname === "/tareas") return true; // Trabajo (la lista)
  if (pathname === "/tareas/aprobar") return true; // carrusel de revisión
  const m = /^\/tareas\/([^/]+)$/.exec(pathname);
  if (m && !SUBRUTAS_TRABAJO_NO_DECISION.has(m[1])) return true; // ficha de una tarea
  return false;
}

export function InterruptBudgetProvider({ children, pathname }) {
  const [active, setActive] = useState(null); // { id, priority } | null
  const queueRef = useRef([]); // pendientes desplazados, mayor prioridad primero
  const blocked = esPantallaDeDecision(pathname || "");

  const request = useCallback((id, priority) => {
    setActive(cur => {
      if (cur?.id === id) return cur; // ya está mostrado
      if (!cur) return { id, priority };
      if (priority > cur.priority) {
        queueRef.current.push(cur);
        return { id, priority };
      }
      if (!queueRef.current.some(q => q.id === id)) queueRef.current.push({ id, priority });
      return cur;
    });
  }, []);

  const release = useCallback((id) => {
    setActive(cur => {
      if (cur?.id !== id) return cur;
      queueRef.current.sort((a, b) => b.priority - a.priority);
      return queueRef.current.shift() || null;
    });
  }, []);

  return (
    <Ctx.Provider value={{ activeId: active?.id ?? null, blocked, request, release }}>
      {children}
    </Ctx.Provider>
  );
}

/**
 * useInterruptSlot(id, priority) — un componente que quiere mostrar una
 * interrupción propia (modal/sheet/franja) llama request() cuando SU
 * condición ya se cumplió (ej. la API dijo que hay NPS pendiente), y
 * release() al cerrarse/completarse. `granted` dice si le toca mostrarse
 * ahora (turno Y pantalla que no sea de decisión).
 *
 * Sin Provider (fallback defensivo): granted siempre true, comportamiento
 * igual al de antes — nunca bloquea una pantalla por un error de wiring.
 */
export function useInterruptSlot(id, priority) {
  const ctx = useContext(Ctx);
  if (!ctx) return { granted: true, request: () => {}, release: () => {} };
  return {
    granted: ctx.activeId === id && !ctx.blocked,
    request: () => ctx.request(id, priority),
    release: () => ctx.release(id),
  };
}

// v8.5.64 · piel · "NPS solo si el cliente ya decidió algo esta sesión" —
// DecisionBar.jsx marca esto al aprobar (la única acción de decisión que
// pasa por un componente compartido hoy; "pedir cambios" se resuelve en un
// panel propio de cada pantalla, fuera de este componente).
const DECISION_KEY = "portal_decidio_sesion";

export function marcarDecisionTomada() {
  try { sessionStorage.setItem(DECISION_KEY, "1"); } catch {}
}

export function huboDecisionEstaSesion() {
  try { return sessionStorage.getItem(DECISION_KEY) === "1"; } catch { return false; }
}
